import create from 'zustand'
import { RegisteredImage, RegistrationCertificate } from '../types'
import { deleteManuallyUploadedImage, fetchRegisteredImages } from '../api'
import Response from '../../../../lib/http/Response'

interface RegisteredImagesState {
	registeredImages: RegisteredImage[]
	selectedRegistrationCertificate: RegistrationCertificate | null
	totalRegisteredImagesCount: number
	pageNumber: number
	hasRegisteredImagesInitiallyLoaded: boolean
	searchString: string
	uploadImagesModalOpen: boolean
	setUploadImagesModalOpen: (uploadImagesModalOpen: boolean) => void
	fetchRegisteredImages: (
		registrationCertificateId: string,
		params?: {
			isInitialLoad?: boolean
			searchString?: string
		},
	) => Promise<Response>
	deleteImageId: ({
		registeredImageId,
		registrationCertificateId,
	}: {
		registeredImageId: string
		registrationCertificateId: string
	}) => Promise<Response | undefined>
	setSearchString: (searchString: string) => void
}

export const useRegisteredImagesStore = create<RegisteredImagesState>(
	(set, get) => ({
		registeredImages: [],
		selectedRegistrationCertificate: null,
		totalRegisteredImagesCount: 0,
		pageNumber: 0,
		hasRegisteredImagesInitiallyLoaded: false,
		searchString: '',
		uploadImagesModalOpen: false,
		setUploadImagesModalOpen: (uploadImagesModalOpen: boolean) => {
			set({
				uploadImagesModalOpen,
			})
		},

		setSearchString: (searchString) => set({ searchString }),

		fetchRegisteredImages: async (
			registrationCertificateId: string,
			params?: {
				isInitialLoad?: boolean
			},
		) => {
			const { pageNumber, registeredImages, searchString } = get()

			const currentPage = params?.isInitialLoad ? 0 : pageNumber
			const perPage = 30

			const registeredImagesResponse = await fetchRegisteredImages({
				registrationCertificateId,
				page: currentPage,
				perPage,
				...(searchString ? { filters: { partnerImageId: searchString } } : {}),
			})

			if (!registeredImagesResponse.isSuccessful) {
				set({
					registeredImages: [],
					totalRegisteredImagesCount: 0,
					pageNumber: 0,
					searchString: '',
					uploadImagesModalOpen: false,
					selectedRegistrationCertificate: null,
				})
				return registeredImagesResponse
			}

			const { data, pagination } =
				registeredImagesResponse.data.registeredImageData
			const { registrationCertificate } = registeredImagesResponse.data
			const { totalItems } = pagination

			const newImages = params?.isInitialLoad
				? data
				: [...registeredImages, ...data]

			set({
				registeredImages: newImages,
				totalRegisteredImagesCount: totalItems,
				pageNumber: currentPage + 1,
				hasRegisteredImagesInitiallyLoaded: true,
				uploadImagesModalOpen: false,
				selectedRegistrationCertificate: registrationCertificate,
			})

			return registeredImagesResponse
		},
		deleteImageId: async ({
			registeredImageId,
			registrationCertificateId,
		}: {
			registeredImageId: string
			registrationCertificateId: string
		}) => {
			const { registeredImages, totalRegisteredImagesCount } = get()

			const response = await deleteManuallyUploadedImage({
				registeredImageId,
				registrationCertificateId,
			})

			if (response.isSuccessful) {
				const newImages = registeredImages.filter(
					(image) => image.registered_image_id !== registeredImageId,
				)
				const newTotalCount = totalRegisteredImagesCount - 1

				set({
					registeredImages: newImages,
					totalRegisteredImagesCount: newTotalCount,
				})
			}
			return response
		},
	}),
)
